import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, FormControl, Select, MenuItem } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams } from '@mui/x-data-grid';
import { translations } from '../../translations/common';
import { tableContainerStyles, tableHeaderStyles, dataGridStyles } from '../common/styles/TableStyles';
import AccessLogModel, { AccessLogView } from '../../core/api/accessLog/AccessLogModel';
import ManagersModel, { ManagerData } from '../../core/api/manager/ManagersModel';

const ROWS_PER_PAGE = 10;

const AccessLogsTable: React.FC = () => {
  const [logs, setLogs] = useState<AccessLogView[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [managers, setManagers] = useState<ManagerData[]>([]);
  const [selectedManagerId, setSelectedManagerId] = useState<string>('');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: ROWS_PER_PAGE,
  });

  const loadManagers = useCallback(async () => {
    const managers = await ManagersModel.listAll();
    setManagers(managers);
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const result = await AccessLogModel.list({
        page: paginationModel.page,
        size: paginationModel.pageSize,
      }, { userId: selectedManagerId });
      setLogs(result.content);
      setTotalCount(result.page.totalElements);
    } finally {
      setLoading(false);
    }
  }, [paginationModel, selectedManagerId]);

  useEffect(() => {
    loadManagers();
    fetchLogs();
  }, [fetchLogs, loadManagers]);

  const handlePaginationChange = useCallback(
    (model: GridPaginationModel) => {
      setPaginationModel(model);
    },
    []
  );
  const handleManagerChange = (event: any) => {
    setSelectedManagerId(event.target.value);
    setPaginationModel(prev => ({ ...prev, page: 0 })); // Reset to first page when filter changes
  };

  const rowsWithIds = logs.map((log, index) => ({ ...log, id: index }));

  const columns: GridColDef[] = [
    {
      field: 'userId',
      headerName: translations.accessLogs.table.columns.userId,
      flex: 1,
      filterable: true,
      type: 'singleSelect',

      valueOptions: managers.map(manager => ({
        value: manager.id,
        label: `${manager.firstName} ${manager.lastName}`
      })),
      renderCell: (params: GridRenderCellParams<AccessLogView>) => (
        <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {((): string => {
            const manager = managers.find(m => m.id === params.row.userId);
            return manager ? `${manager.firstName} ${manager.lastName}` : 'Unknown';
          })()}
        </Box>
      )
    },
    { field: 'ipAddress', filterable: false, headerName: translations.accessLogs.table.columns.ipAddress, flex: 1 },
    { field: 'clientName', filterable: false, headerName: translations.accessLogs.table.columns.clientName, flex: 1 },
    { field: 'accessTime', filterable: false, headerName: translations.accessLogs.table.columns.accessTime, flex: 1 },
  ];

  const renderTableHeader = () => {
    return (
      <Box sx={{
        ...tableHeaderStyles,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2
      }}>
        <Typography variant="h6">{translations.accessLogs.table.title}</Typography>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={selectedManagerId}
            onChange={handleManagerChange}
            displayEmpty
            size="small"
          >
            <MenuItem value="">
              {translations.accessLogs.table.filters.allManagers}
            </MenuItem>
            {managers.map((manager) => (
              <MenuItem key={manager.id} value={manager.id}>
                {`${manager.firstName} ${manager.lastName}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  };

  return (
    <Box sx={tableContainerStyles}>
      {renderTableHeader()}
      <DataGrid
        localeText={{
          filterOperatorIs: "yra",
        }}
        rows={rowsWithIds}
        columns={columns.map(col => ({ ...col, sortable: false }))}
        loading={loading}
        rowCount={totalCount}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
        paginationMode="server"
        pageSizeOptions={[ROWS_PER_PAGE]}
        disableRowSelectionOnClick
        slots={{
          noRowsOverlay: () => (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              {translations.accessLogs.table.noData}
            </Box>
          ),
        }}
        sx={dataGridStyles}
      />
    </Box>
  );
};

export default AccessLogsTable;
